import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgxsFormDirective } from '@ngxs/form-plugin';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AcademicYearState } from '../academic-year/academic-year.state';
import { NumberLimitToDirective } from '../directives/number-limit-to.directive';
import { TrimValueDirective } from '../directives/trim-value.directive';
import { GuardianState } from '../guardian/guardian.state';
import { AddressOrNamePipe } from '../pipes/address-or-name.pipe';
import { DotNetDatePipe } from '../pipes/dot-net-date.pipe';
import { FullNamePipe } from '../pipes/full-name.pipe';
import { PlaceholderPipe } from '../pipes/placeholder.pipe';
import { Placeholder2Pipe } from '../pipes/placeholder2.pipe';
import { PossessivePipe } from '../pipes/possessive.pipe';
import { RootState } from '../store/root.state';
import { Guardian } from '../types';
import { IApplicationContent } from '../types/content.type';
import { INearbySchool } from '../types/nearby-school.type';
import { BirthDateValidator } from '../validators/birthdate.validator';
import { PreferredLanguageValidator } from '../validators/preferred-language.validator';
import { ResidencyValidator } from '../validators/residency.validator';
import { SafeValueValidator } from '../validators/safe-value.validator';
import { TooYoungValidator } from '../validators/too-young.validator';

declare const NHAUtil: any;

@Component({
	selector: 'app-child',
	templateUrl: './child.component.html',
	styleUrls: ['./child.component.scss'],
	imports: [
		NgIf,
		FormsModule,
		NgxsFormDirective,
		ReactiveFormsModule,
		NgFor,
		NgClass,
		TrimValueDirective,
		NumberLimitToDirective,
		AsyncPipe,
		FullNamePipe,
		AddressOrNamePipe,
		PossessivePipe,
		PlaceholderPipe,
		Placeholder2Pipe
	]
})
export class ChildComponent implements OnInit, AfterViewInit {
	content$: Observable<IApplicationContent>;

	childForm: UntypedFormGroup;
	internalGuardians: Guardian[];
	academicYearApplying = 0;
	nearbySchoolsApplying: INearbySchool[];
	currentYear: number = new Date().getFullYear();
	appContent: IApplicationContent;

	showFRLQuestion: boolean;
	showELLQuestion: boolean;
	showSPEDQuestion: boolean;
	schoolIsNY: boolean;

	constructor(
		private fb: UntypedFormBuilder,
		private store: Store,
		private dotnetdatepipe: DotNetDatePipe
	) {}

	ngOnInit(): void {
		this.appContent = this.store.selectSnapshot(RootState.content);
		this.schoolIsNY = this.appContent.stateCode === 'NY';

		this.createForm();

		this.content$ = this.store.select(RootState.content).pipe(
			tap((content) => {
				this.appContent = content;

				if (this.appContent.stateCode === 'NC' || this.appContent.stateCode === 'NY') {
					this.children.controls.forEach((c) => {
						(c as UntypedFormGroup).removeControl('genderId');
					});
				}
			})
		);

		this.store.select(GuardianState.guardians).subscribe((guardians) => {
			if (guardians) {
				this.internalGuardians = guardians;
				this.updateGuardianFormFields(guardians);
			}
		});

		this.store.select(AcademicYearState.selectedYear).subscribe((year) => {
			if (year) {
				this.academicYearApplying = year;

				this.showELLQuestion =
					this.academicYearApplying === this.appContent.currentAcademicYear
						? this.appContent.showEnglishLanguageLearnerQuestionCurrentAcademicYear
						: this.appContent.showEnglishLanguageLearnerQuestionNextAcademicYear;

				this.showFRLQuestion =
					this.academicYearApplying === this.appContent.currentAcademicYear
						? this.appContent.showFreeReducedLunchQuestionCurrentAcademicYear
						: this.appContent.showFreeReducedLunchQuestionNextAcademicYear;

				this.showSPEDQuestion =
					this.academicYearApplying === this.appContent.currentAcademicYear
						? this.appContent.showSpecialEducationQuestionCurrentAcademicYear
						: this.appContent.showSpecialEducationQuestionNextAcademicYear;

				this.resetGradeLevelControls();
				setTimeout(() => {
					NHAUtil.configureTooltips();
					NHAUtil.configureAutocomplete();
				}, 100);
			}
		});
	}

	ngAfterViewInit(): void {
		NHAUtil.configureTooltips();
		NHAUtil.configureAutocomplete();
	}

	get children(): UntypedFormArray {
		return this.childForm.get('children') as UntypedFormArray;
	}

	childGroup(i: number): UntypedFormGroup {
		return this.children.controls[i] as UntypedFormGroup;
	}

	getControl(i: number, controlName: string): UntypedFormControl {
		return this.children.controls[i].get(controlName) as UntypedFormControl;
	}

	getArray(i: number, arrayName: string): UntypedFormArray {
		return this.children.controls[i].get(arrayName) as UntypedFormArray;
	}

	getArrayControl(i: number, arrayName: string, j: number, controlName: string): UntypedFormControl {
		return (this.children.controls[i].get(arrayName) as UntypedFormArray).controls[j].get(controlName) as UntypedFormControl;
	}

	createForm(): void {
		this.childForm = this.fb.group({
			children: this.fb.array([])
		});

		this.addChild();
	}

	addChild(): void {
		const newChild = this.fb.group(
			{
				firstName: ['', [Validators.required, Validators.maxLength(100), SafeValueValidator]],
				middleName: ['', [SafeValueValidator, Validators.maxLength(100)]],
				lastName: ['', [Validators.required, Validators.maxLength(100), SafeValueValidator]],
				suffixId: ['', SafeValueValidator],
				birthMonth: ['', [Validators.required, SafeValueValidator]],
				birthDay: ['', [Validators.required, Validators.max(31), Validators.min(1), SafeValueValidator]],
				birthYear: ['', [Validators.required, Validators.max(this.currentYear), Validators.min(this.currentYear - 20), SafeValueValidator]],
				genderId: ['', [Validators.required, SafeValueValidator]],
				displayKindergartenWaiver: false,
				isKindergartenWaiverApproved: false,
				guardianRelationships: this.fb.array([]),
				householdResidencies: this.fb.array([], ResidencyValidator(this.schoolIsNY)),
				isEnglishLanguageLearner: [null, SafeValueValidator],
				isEligibleForFreeAndReducedLunch: [null, SafeValueValidator],
				isSpecialEducation: [null, SafeValueValidator],
				gradeLevelId: [
					'',
					[Validators.required, TooYoungValidator(this.children.length, this.appContent, this.store, this.dotnetdatepipe), SafeValueValidator]
				],
				isSelfPaced: [null, this.appContent.schoolID === 509 ? [Validators.required, SafeValueValidator] : [SafeValueValidator]],
				receivedSpecialEducationServices: [null, SafeValueValidator],
				previousSchool: ['', SafeValueValidator],
				nearbySchoolsAdded: this.fb.array([]),
				applicationForSchools: this.fb.array([]),
				preferredLanguage: ['', PreferredLanguageValidator(this.children.length, this.appContent.languages)],
				preferredLanguageId: '',
				matriculatingSchools: [[], SafeValueValidator],
				isFreeReducedLunchMigrantHomelessPrimaryLanguageNotEnglishDisabilityIEP: null,
				// used for setting overall date
				birthDate: ''
			},
			{
				validators: [Validators.compose(<any>[BirthDateValidator])]
			}
		);

		if (this.children.length > 0) {
			this.internalGuardians.forEach((guardian) => {
				(newChild.controls.guardianRelationships as UntypedFormArray).push(this.addGuardianRelationship(guardian));
				const hhrArray = newChild.controls.householdResidencies as UntypedFormArray;
				if (hhrArray.controls.filter((control) => control.get('internalHouseholdId').value === guardian.household.internalHouseholdId).length < 1) {
					hhrArray.push(this.addHouseholdResidency(guardian, this.internalGuardians.indexOf(guardian) + 1));
				}
			});
		}

		if (this.appContent.stateCode === 'NC' || this.appContent.stateCode === 'NY') {
			newChild.removeControl('genderId');
		}

		this.children.push(newChild);

		setTimeout(() => {
			NHAUtil.configureTooltips();
			NHAUtil.configureAutocomplete();
		}, 500);
	}

	addGuardianRelationship(guardian: Guardian): UntypedFormGroup {
		const relationshipForm = this.fb.group({
			internalGuardianId: null,
			firstName: '',
			lastName: '',
			relationshipTypeId: ['', [Validators.required, SafeValueValidator]]
		});

		if (guardian) {
			relationshipForm.patchValue({
				internalGuardianId: guardian.internalGuardianId,
				firstName: guardian.firstName,
				lastName: guardian.lastName
			});
		}

		return relationshipForm;
	}

	addHouseholdResidency(guardian: Guardian, idx: number): UntypedFormGroup {
		const residencyForm = this.fb.group({
			internalGuardianDisplayIdx: null,
			internalHouseholdId: null,
			firstName: '',
			lastName: '',
			address: '',
			residencyTypeId: ['', [Validators.required, SafeValueValidator]]
		});

		if (guardian) {
			residencyForm.patchValue({
				internalGuardianDisplayIdx: idx,
				internalHouseholdId: guardian.household.internalHouseholdId,
				firstName: guardian.firstName,
				lastName: guardian.lastName,
				address: guardian.household.address
			});

			if (this.schoolIsNY && idx === 1) {
				residencyForm.get('residencyTypeId').clearValidators();
			}
		}

		return residencyForm;
	}

	removeChild(i: number): void {
		this.children.removeAt(i);
	}

	showReceivedSpecialEd(): boolean {
		return this.academicYearApplying >= 2019 && this.appContent.stateCode === 'LA' && this.appContent.schoolID !== 380 ? true : false;
	}

	updateGuardianFormFields(guardians: Guardian[]): void {
		this.children.controls.forEach((child) => {
			const relationshipsFormArray = child.get('guardianRelationships') as UntypedFormArray;
			const residenciesFormArray = child.get('householdResidencies') as UntypedFormArray;

			relationshipsFormArray.controls.forEach((control) => {
				const existingGuardian = guardians.filter((g) => g.internalGuardianId === control.get('internalGuardianId').value);

				if (existingGuardian.length > 0) {
					(control as UntypedFormGroup).controls.firstName.setValue(existingGuardian[0].firstName);
					(control as UntypedFormGroup).controls.lastName.setValue(existingGuardian[0].lastName);
				} else {
					relationshipsFormArray.removeAt(relationshipsFormArray.controls.indexOf(control));
				}
			});

			residenciesFormArray.controls.forEach((control) => {
				const existingHousehold = guardians.filter((g) => g.household.internalHouseholdId === control.get('internalHouseholdId').value);

				if (existingHousehold.length > 0) {
					(control as UntypedFormGroup).controls.address.setValue(existingHousehold[0].household.address);
					(control as UntypedFormGroup).controls.firstName.setValue(existingHousehold[0].firstName);
					(control as UntypedFormGroup).controls.lastName.setValue(existingHousehold[0].lastName);
				} else {
					residenciesFormArray.removeAt(residenciesFormArray.controls.indexOf(control));
				}
			});

			// add if the form arrays don't have a value
			guardians.forEach((guardian) => {
				if (!relationshipsFormArray.controls.find((x) => x.get('internalGuardianId').value === guardian.internalGuardianId)) {
					relationshipsFormArray.push(this.addGuardianRelationship(guardian));
				}
				if (!residenciesFormArray.controls.find((x) => x.get('internalHouseholdId').value === guardian.household.internalHouseholdId)) {
					residenciesFormArray.insert(guardians.indexOf(guardian), this.addHouseholdResidency(guardian, guardians.indexOf(guardian) + 1));
				}
			});
		});
	}

	mapApplicationForSchools(enrollmentPreferenceValue: number): void {
		this.children.controls.forEach((child) => {
			const idx = this.children.controls.indexOf(child);
			const appForSchoolsArray = child.get('applicationForSchools') as UntypedFormArray;
			const nearbySchoolsAddedArray = child.get('nearbySchoolsAdded') as UntypedFormArray;

			// angular gets angry if you reset the form array. need to just remove items in a loop.
			for (let i = appForSchoolsArray.length; i >= 0; i--) {
				appForSchoolsArray.removeAt(i);
			}

			appForSchoolsArray.push(
				this.fb.group({
					schoolId: this.appContent.schoolID,
					gradeLevelId: (child as UntypedFormGroup).controls.gradeLevelId.value,
					enrollmentPreferenceId: enrollmentPreferenceValue
				})
			);
		});
	}

	resetGradeLevelControls(): void {
		this.children.controls.forEach((child) => {
			const idx = this.children.controls.indexOf(child);

			child.get('gradeLevelId').setValue('');

			if (idx !== 0) {
				const nearbySchoolsAddedArray = child.get('nearbySchoolsAdded') as UntypedFormArray;

				nearbySchoolsAddedArray.controls.forEach((group) => {
					group.get('gradeLevelId').setValue('');
				});
			}

			this.checkForKindergartenWaiver(idx);
		});
	}

	validateGradeLevelControlsForChild(idx: number): void {
		const childFormGroup = this.children.controls[idx] as UntypedFormGroup;
		const gradeControl = childFormGroup.get('gradeLevelId');

		if (gradeControl.value !== '') {
			gradeControl.updateValueAndValidity();
		}

		this.checkForKindergartenWaiver(idx);
	}

	validateAllGradeLevelControls(): void {
		this.children.controls.forEach((child) => {
			const idx = this.children.controls.indexOf(child);
			this.validateGradeLevelControlsForChild(idx);
		});
	}

	checkForKindergartenWaiver(idx: number): void {
		if (this.appContent.stateCode === 'MI') {
			const month = this.childGroup(idx).controls['birthMonth'].value;
			const day = this.childGroup(idx).controls['birthDay'].value ? this.childGroup(idx).controls['birthDay'].value.toString() : '';
			const year = this.childGroup(idx).controls['birthYear'].value ? this.childGroup(idx).controls['birthYear'].value.toString() : '';

			if (month && day && year && year.length === 4) {
				const birthDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
				const september1 = new Date(this.academicYearApplying - 5, 8, 1);
				const december1 = new Date(this.academicYearApplying - 5, 11, 1);

				if (september1 <= birthDate && birthDate <= december1) {
					this.childGroup(idx).controls['displayKindergartenWaiver'].setValue(true);
					this.childGroup(idx).controls['isKindergartenWaiverApproved'].setValidators([Validators.requiredTrue]);
				} else {
					this.childGroup(idx).controls['displayKindergartenWaiver'].setValue(false);
					this.childGroup(idx).controls['isKindergartenWaiverApproved'].setValue(false);
					this.childGroup(idx).controls['isKindergartenWaiverApproved'].clearValidators();
					this.childGroup(idx).controls['isKindergartenWaiverApproved'].updateValueAndValidity();
				}
			}
		}
	}

	mapBirthDates(): void {
		this.children.controls.forEach((child) => {
			const month = parseInt(child.get('birthMonth').value);
			const day = parseInt(child.get('birthDay').value);
			const year = parseInt(child.get('birthYear').value);
			child.get('birthDate').setValue(month + '/' + day + '/' + year);
		});
	}

	getLanguageList(): any {
		if (this.appContent.languages != null) {
			return JSON.stringify(this.appContent.languages.map((language) => language.name));
		}

		return null;
	}

	mapLanguages(): void {
		this.children.controls.forEach((child) => {
			if (child.get('preferredLanguage').value) {
				const langMatch = this.appContent.languages.filter((lang) => lang.name === child.get('preferredLanguage').value);

				if (langMatch) {
					child.get('preferredLanguageId').setValue(langMatch[0].id);
				}
			}
		});
	}

	mapMatriculationSchools(childIndex: number, school: number, checked: boolean): void {
		const matriculatingSchoolIDs = this.children.controls[childIndex]?.get('matriculatingSchools')?.value as Array<number> | null;

		if (matriculatingSchoolIDs === null) {
			return;
		}

		if (!checked && matriculatingSchoolIDs.includes(school)) {
			matriculatingSchoolIDs.splice(matriculatingSchoolIDs.indexOf(school), 1);
		} else if (checked && matriculatingSchoolIDs.indexOf(school) === -1) {
			matriculatingSchoolIDs.push(school);
		}

		this.children.controls[childIndex].get('matriculatingSchools').setValue(matriculatingSchoolIDs);
	}
}
